import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { postalCodeRegexPatternForCountry } from "@faire/web/ui/Addresses/addressSchema";

import { BUSINESS_TYPE } from "@faire/retailer/consts/BUSINESS_TYPE";
import { postalCodeFormat } from "@faire/retailer/consts/postalCodeFormat";
import { SIGN_UP_INPUT_FIELD_ERROR } from "@faire/retailer/consts/sign-up-input-field-error";
import { isValidEmail, isValidPublicUrl } from "@faire/retailer/lib/validation";
import { getIsProduction } from "@faire/retailer/serialized-data/getEnvName";
import { SSOStore } from "@faire/retailer/stores/domain/SSOStore";

const PASSWORD_MIN_LENGTH = 8;
const STORE_MIN_LENGTH = 3;
const URL_MAX_LENGTH = 255;
const POSTAL_CODE_MAX_LENGTH = 10;
const BUSINESS_DESCRIPTION_MAX_LENGTH = 250;
const FIRST_OR_LAST_NAME_MAX_LENGTH = 255;

export const isInvalidValue = (value: string) => {
  if (!value || value.length === 0) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }
  return false;
};

export const isInvalidName = (value: string) => {
  if (!value || value.length === 0) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  } else if (value.length > FIRST_OR_LAST_NAME_MAX_LENGTH) {
    return SIGN_UP_INPUT_FIELD_ERROR.LARGER_THAN_MAXIMUM;
  }
  return false;
};

export const isInvalidEmail = (email: string) => {
  if (!email || email.length === 0) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }
  if (!isValidEmail(email)) {
    return SIGN_UP_INPUT_FIELD_ERROR.INVALID_VALUE;
  }

  return false;
};

export const isFaireEmail = (email: string) => {
  if (!email || email.length === 0) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }
  if (
    !isValidEmail(email) ||
    (getIsProduction() &&
      !email.includes("+") &&
      email.trim().endsWith("@faire.com"))
  ) {
    return SIGN_UP_INPUT_FIELD_ERROR.INVALID_VALUE;
  }

  return false;
};

export const isInvalidPassword = (password: string) => {
  if (SSOStore.get().isActive) {
    return false;
  }
  if (!password || password.length === 0) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  } else if (password.length < PASSWORD_MIN_LENGTH) {
    return SIGN_UP_INPUT_FIELD_ERROR.LESS_THAN_MINIMUM;
  }
  return false;
};

export const isInvalidCountry = (country: string) => {
  if (!country || country.length === 0) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }
  return false;
};

export const isInvalidLocaleKey = (localeKey: string) => {
  if (!localeKey || localeKey.length === 0) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }
  return false;
};

export const isInvalidStore = (value: string) => {
  if (!value) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }

  if (value.trim().length < STORE_MIN_LENGTH) {
    return SIGN_UP_INPUT_FIELD_ERROR.LESS_THAN_MINIMUM;
  }

  return false;
};

export const isInvalidWebsite = (url: string, optional?: boolean) => {
  if (optional && (!url || url.length === 0)) {
    return false;
  }

  if (!url) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }

  if (url.length > URL_MAX_LENGTH) {
    return SIGN_UP_INPUT_FIELD_ERROR.LARGER_THAN_MAXIMUM;
  }

  if (!isValidPublicUrl(url)) {
    return SIGN_UP_INPUT_FIELD_ERROR.INVALID_VALUE;
  }

  return false;
};

export const isInvalidBusinessType = (businessType: BUSINESS_TYPE) => {
  if (!businessType) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }

  return false;
};

export const isInvalidPostalCode = (
  postalCode: string,
  strictLocalize: StrictLocalizeFunction,
  countryIso?: string
) => {
  if (!postalCode || postalCode.length === 0) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }
  // Default to the US
  if (!countryIso && postalCode.length > POSTAL_CODE_MAX_LENGTH) {
    return SIGN_UP_INPUT_FIELD_ERROR.INVALID_VALUE;
  }

  if (
    postalCode.match(
      postalCodeRegexPatternForCountry(countryIso, strictLocalize) ??
        postalCodeFormat
    ) === null
  ) {
    return SIGN_UP_INPUT_FIELD_ERROR.INVALID_VALUE;
  }

  return false;
};

export const isInvalidBusinessDescription = (description: string) => {
  if (description.length === 0) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }
  if (description.length > BUSINESS_DESCRIPTION_MAX_LENGTH) {
    return SIGN_UP_INPUT_FIELD_ERROR.LARGER_THAN_MAXIMUM;
  }

  return false;
};

export const isInvalidNumberValue = (value?: number) => {
  if (value == null || isNaN(value)) {
    return SIGN_UP_INPUT_FIELD_ERROR.MISSING;
  }
  return false;
};
